/**
 * The add-players drawer's two lifted controls: the Suggested tab's reason
 * filter and the drawer-wide GPR sort (SE-6554).
 *
 * Both live here rather than inside a tab body because the CONTROLS render in
 * the panel chrome while the DATA lives in the tab bodies. The panel derives
 * the tab counts from the same helpers the bodies render with, so the badge on
 * a tab and the list inside it can never disagree — a count computed by a
 * second, parallel expression is a count that drifts.
 *
 * The reason vocabulary is deliberately NOT enumerated here. See
 * `constants/suggestionReasons` for why the options are derived from the
 * candidates in hand instead of from the PRD's five hardcoded labels.
 * @module features/player-kanban/utils/addPlayersDrawerControls
 */

import type { SuggestionReasonCategory } from "@/generated/graphql";

/** The minimum a candidate must expose to be reason-filtered. */
export interface ReasonFilterable {
  readonly reasons: readonly SuggestionReasonCategory[];
}

/**
 * The filters panel's REASON group selection (SE-6949) — multi-select, EMPTY
 * MEANS NOT APPLIED. Reasons exist only on suggestion candidates, so this
 * group is evaluated client-side over the suggestions in hand; see
 * {@link filterByReasons}.
 */
export type ReasonSelection = readonly SuggestionReasonCategory[];

/**
 * Toggle one reason category in the selection.
 * @param current - The selected categories.
 * @param category - The category whose chip was pressed.
 * @returns A new array with that category added or removed.
 */
export const toggleReason = (
  current: ReasonSelection,
  category: SuggestionReasonCategory
): SuggestionReasonCategory[] =>
  current.includes(category)
    ? current.filter(entry => entry !== category)
    : [...current, category];

/**
 * Narrow candidates to those carrying ANY selected reason category.
 *
 * Filtered CLIENT-SIDE over the candidates the query already returned: the
 * suggestions read accepts no reason argument, and every candidate already
 * carries its own `reasons`, so a server round-trip would buy nothing. An
 * empty selection means the group is not applied and returns everything.
 * @param candidates - The loaded candidates.
 * @param selection - The selected categories, possibly empty.
 * @returns The candidates carrying at least one selected reason, or all of
 * them when the selection is empty.
 */
export const filterByReasons = <T extends ReasonFilterable>(
  candidates: readonly T[],
  selection: ReasonSelection
): readonly T[] =>
  selection.length === 0
    ? candidates
    : candidates.filter(candidate =>
        candidate.reasons.some(reason => selection.includes(reason))
      );
